'use client';

import { useState } from 'react';
import { motion } from 'framer-motion';
import type { TestimonialsModel } from '@/lib/mappers/testimonialsMapper';

interface TestimonialsSectionServerProps {
  initialData: TestimonialsModel;
  dict: any;
}

const TestimonialsSectionServer = ({ initialData, dict }: TestimonialsSectionServerProps) => {
  const [currentTestimonial, setCurrentTestimonial] = useState(0);

  const nextTestimonial = () => {
    setCurrentTestimonial((prev) => (prev + 1) % initialData.testimonials.length);
  };

  const prevTestimonial = () => {
    setCurrentTestimonial((prev) => (prev - 1 + initialData.testimonials.length) % initialData.testimonials.length);
  };

  return (
    <section 
      className="py-20"
      style={{ backgroundColor: initialData.styling.backgroundColor }}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <motion.p
            className="font-semibold uppercase tracking-wide mb-2"
            style={{ color: '#4670BF', fontSize: '15px' }}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            {initialData.badge}
          </motion.p>
          <motion.h2
            className="font-extrabold programs-title-dark"
            style={{ fontSize: '37px' }}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            viewport={{ once: true }}
          >
            {initialData.title}
          </motion.h2>
        </div>

        {/* Testimonials Carousel */}
        <div className="relative">
          {/* Navigation Arrows */}
          {initialData.navigation.showArrows && (
            <>
              <button
                onClick={prevTestimonial}
                className="absolute left-0 top-1/2 transform -translate-y-1/2 z-10 bg-white rounded-full p-3 shadow-lg hover:shadow-xl transition-shadow duration-200"
              >
                <svg className="w-6 h-6 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>
              
              <button
                onClick={nextTestimonial}
                className="absolute right-0 top-1/2 transform -translate-y-1/2 z-10 bg-white rounded-full p-3 shadow-lg hover:shadow-xl transition-shadow duration-200"
              >
                <svg className="w-6 h-6 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            </>
          )}

          {/* Testimonials Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {initialData.testimonials.map((testimonial) => (
              <div 
                key={testimonial.id} 
                className="bg-white rounded-xl shadow-lg hover:shadow-xl transition-shadow duration-200 p-8"
                style={{ backgroundColor: initialData.styling.cardBackgroundColor }}
              >
                {/* Quote Icon */}
                {initialData.layout.showQuoteIcon && (
                  <div className="mb-6">
                    <svg 
                      className="w-8 h-8" 
                      fill="currentColor" 
                      viewBox="0 0 24 24"
                      style={{ color: initialData.styling.quoteColor }}
                    >
                      <path d="M14.017 21v-7.391c0-5.704 3.731-9.57 8.983-10.609l.995 2.151c-2.432.917-3.995 3.638-3.995 5.849h4v10h-9.983zm-14.017 0v-7.391c0-5.704 3.748-9.57 9-10.609l.996 2.151c-2.433.917-3.996 3.638-3.996 5.849h4v10h-10z"/>
                    </svg>
                  </div>
                )}

                {/* Testimonial Text */}
                <blockquote 
                  className="text-body-lg mb-6"
                  style={{ color: initialData.styling.textColor }}
                >
                  "{testimonial.text}"
                </blockquote>

                {/* Author */}
                <div 
                  className="border-t pt-4"
                  style={{ borderColor: initialData.styling.borderColor }}
                >
                  <p 
                    className="font-semibold text-sm"
                    style={{ color: initialData.styling.authorColor }}
                  >
                    {testimonial.author}
                  </p>
                  <p 
                    className="text-sm"
                    style={{ color: initialData.styling.roleColor }}
                  >
                    {testimonial.role}
                  </p>
                  
                  {/* Optional Rating */}
                  {initialData.layout.showRating && (
                    <div className="flex items-center mt-2">
                      {[...Array(5)].map((_, i) => (
                        <svg
                          key={i}
                          className={`w-4 h-4 ${i < testimonial.rating ? 'text-yellow-400' : 'text-gray-300'}`}
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                        </svg>
                      ))}
                    </div>
                  )}
                  
                  {/* Optional Date */}
                  {initialData.layout.showDate && (
                    <p 
                      className="text-xs mt-1"
                      style={{ color: initialData.styling.roleColor }}
                    >
                      {new Date(testimonial.date).toLocaleDateString()}
                    </p>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default TestimonialsSectionServer;
